/**
 * REST API client for web.
 * Use for unauthenticated requests.
 *
 * It is recommented to use useQuery() from @tanstack/react-query instead of this client directly.
 */

import axios from "axios"
import {
  AiFeedbackBody,
  BaseError,
  GQL,
  NotificationInviteCodeRequestBody,
  NotificationSupportRequestBody,
  NotificationTomboContactDemo,
  NotificationsApplyForJob,
  CandidateProfilePublic,
} from "platform-common/index"
import { getFreshAccessToken, getSsrFreshAccessToken } from "./auth"
import { RestUser, ValidateInviteRequest, ValidateInviteResponse } from "platform-common/types/restApi"
import { GetServerSidePropsContext } from "next"

export class TooManyRequestsError extends BaseError {
  constructor() {
    super("Too many requests")
  }
}

export const BASE_URL = process.env.NEXT_PUBLIC_API_URL || "https://UNCONFIGURED"

export const API_ROUTER = {
  sendNotification: () => `${BASE_URL}/notifications`,
  approveCandidate: () => `${BASE_URL}/candidate/approve`,
  getCandidateProfile: () => `${BASE_URL}/candidate/profile`,
  sendUserAuthenticatedSlackNotification: () => `${BASE_URL}/notifications/auth`,
  validateInvite: () => `${BASE_URL}/auth/invite/validate`,
  getUser: () => `${BASE_URL}/auth/getUser`,
  getCandidatePublicProfile: () => `${BASE_URL}/candidate/public/profile`,
  getBillingPlans: () => `${BASE_URL}/billing/plans`,
  notifications: () => `${BASE_URL}/notifications`,
}

type GetCandidateProfileArgs =
  | {
      candidateId: string
      slug?: never
    }
  | {
      candidateId?: never
      slug: string
    }

export type ApproveCandidateArgs = {
  calendarLink: string
  candidateLink: string
  clientInfo: string
}

type UserAddress = {
  ip: string
  city: string
  country: string
}

type GetPublicCandidateProfile = {
  shareableLinkId: string
  userId: string | null | undefined
  username: string | null | undefined
  userAddress: UserAddress
}

export const getPublicCandidateProfile = async ({
  shareableLinkId,
  username,
  userId,
  userAddress,
}: GetPublicCandidateProfile) => {
  const response = await axios.post<CandidateProfilePublic>(
    API_ROUTER.getCandidatePublicProfile(),
    {
      userId,
      shareableLinkId,
      username,
    },
    {
      headers: {
        "x-forwarded-for": userAddress.ip,
        "x-user-city": userAddress.city,
        "x-user-country": userAddress.country,
      },
    }
  )
  return response.data
}

export const approveCandidate = async (args: ApproveCandidateArgs) => {
  return await axios.post(API_ROUTER.approveCandidate(), args)
}

export const unsubscribeCandidate = async (args: { emailId: string; userId: string }) => {
  return await axios.post(`${BASE_URL}/unsubscribe`, args)
}
export const getCandidateProfile = async (
  args: GetCandidateProfileArgs
): Promise<GQL.CandidateProfileOutput | null> => {
  const { candidateId, slug } = args
  const result = await axios.get<GQL.CandidateProfileOutput>(API_ROUTER.getCandidateProfile(), {
    params: { candidateId, slug: `/${slug}` },
    headers: {
      Authorization: `Bearer ${await getFreshAccessToken()}`,
    },
  })
  return result.data || null
}

export const validateInvite = async (params: ValidateInviteRequest): Promise<ValidateInviteResponse> => {
  const result = await axios.post<ValidateInviteResponse>(API_ROUTER.validateInvite(), params)
  return result.data
}

export const getUser = async ({ req }: GetServerSidePropsContext) => {
  return await axios.get<RestUser>(API_ROUTER.getUser(), {
    headers: { Authorization: `Bearer ${await getSsrFreshAccessToken(req)}` },
  })
}

export const applyForJobTomboHomePage = async (params: NotificationsApplyForJob) => {
  return await axios.post(API_ROUTER.sendNotification(), params)
}

export const sendTomboHomePageMessage = async (params: NotificationTomboContactDemo) => {
  return await axios.post(API_ROUTER.sendNotification(), params)
}

export const askForInviteCode = async (params: NotificationInviteCodeRequestBody) => {
  return await axios.post(API_ROUTER.sendNotification(), params)
}

export const sendMess = async (params: NotificationInviteCodeRequestBody) => {
  return await axios.post(API_ROUTER.sendNotification(), params)
}
export const sendAiFeedback = async (params: AiFeedbackBody) => {
  return await axios.post(API_ROUTER.sendNotification(), params)
}

export const sendSupportRequest = async (params: NotificationSupportRequestBody) => {
  return await axios.post(API_ROUTER.sendNotification(), params)
}
